import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { buildAdornmentTextFieldSx, fieldLabelSx } from '../common/formFieldStyles';
import { formatCurrency, getCurrencySymbol } from '../../utils/currencyUtils';
import { formatServicePriceLabel, isPriceOutsideServiceRange } from '../../utils/serviceValueFormat';

const SetAppointmentServicePriceDialog = ({
  open,
  service,
  currencyCode,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [price, setPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setPrice('');
    setError('');
    setSaving(false);
  }, [open, service?.serviceId]);

  const rangeLabel = formatServicePriceLabel(
    {
      priceMode: service?.priceMode,
      price: service?.catalogPrice,
      priceMax: service?.priceMax,
    },
    {
      formatMoney: (amount) => formatCurrency(amount, currencyCode),
      onRequestLabel: t('servicesManagement.valueMode.onRequest'),
      fromLabel: (formatted) => t('servicesManagement.valueMode.priceFrom', { price: formatted }),
    },
  );
  const parsed = price === '' ? null : Number(price);
  const canSave = parsed != null && Number.isFinite(parsed) && parsed >= 0;
  const outsideRange = canSave && isPriceOutsideServiceRange(service, parsed);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError('');
    try {
      await onSave(parsed);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || t('managementAppointments.setPrice.error'));
      setSaving(false);
    }
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      title={t('managementAppointments.setPrice.title')}
      maxWidth="sm"
      showCloseButton
      dividers
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button color="inherit" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleSave} disabled={!canSave || saving}>
            {t('managementAppointments.setPrice.save')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Typography variant="subtitle2" sx={fieldLabelSx}>
        {t('managementAppointments.setPrice.price')}
      </Typography>
      <TextField
        type="number"
        value={price}
        onChange={(event) => setPrice(event.target.value)}
        placeholder={rangeLabel}
        fullWidth
        inputProps={{
          min: 0,
          step: 0.01,
          'aria-label': t('managementAppointments.setPrice.price'),
        }}
        InputProps={{
          endAdornment: <InputAdornment position="end">{getCurrencySymbol(currencyCode)}</InputAdornment>,
        }}
        sx={buildAdornmentTextFieldSx(false)}
      />
      {outsideRange && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('managementAppointments.setPrice.outsideRange', { range: rangeLabel })}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </ScrollableDialog>
  );
};

export default SetAppointmentServicePriceDialog;
